<script>
import Form from '@/components/ui/widgets/Form.vue'
import FormSection from '@/components/ui/widgets/FormSection.vue'
import { getNotificationLog } from '@/api/cars'
import ActionButtonColumn from '@/components/ui/datatable/columns/ActionButtonColumn.vue'
import LinkColumn from '@/components/ui/datatable/columns/LinkColumn.vue'
import { isEmpty } from '@/helpers/utils'
import { DateTime } from 'luxon'
import NotificationPreviewModal from '@/components/notifications/NotificationPreviewModal.vue'
import NotificationContextModal from '@/components/notifications/NotificationContextModal.vue'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import ExcelExportButton from '@/components/ui/buttons/ExcelExportButton.vue'

export default {
  components: {
    'mia-excel-export-button': ExcelExportButton,
    'mia-feature-input': FeatureInput,
    'mia-form': Form,
    'mia-form-section': FormSection,
    'mia-notification-context-modal': NotificationContextModal,
    'mia-notification-preview-modal': NotificationPreviewModal
  },

  props: {
    carId: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      previewableNotification: null,
      previewableMetadataNotification: null,
      columns: [
        {
          field: 'id',
          label: 'ID',
          headerAlign: 'left'
        },
        {
          field: 'item_created_at',
          label: 'Versandt am',
          headerAlign: 'left',
          representedAs: row => DateTime.fromSQL(row.item_created_at).toFormat('dd.MM.yyyy, HH:mm \'Uhr\'')
        },
        {
          field: 'item_notification',
          label: 'Benachrichtigung',
          headerAlign: 'left',
          representedAs: row => row.item_notification_label
        },
        {
          field: 'item_customer',
          label: 'Kunde',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            labelProp: row => this.getCustomerName(row),
            schema: '/customers/:item_customer',
            schemaIds: row => ({ item_customer: row.item_metadata.customerId }),
            external: true,
            highlight: () => this.query
          }
        },
        {
          field: '',
          label: 'Aktion',
          headerAlign: 'right',
          align: 'right',
          sortable: false,
          filterable: false,
          component: ActionButtonColumn,
          meta: {
            actions: (row) => {
              const baseActions = [
                {
                  label: 'Vorschau',
                  attrs: {
                    title: 'Vorschau'
                  },
                  onClick: (r) => {
                    this.previewableNotification = r
                  }
                }
              ]

              return row.item_context && !Array.isArray(row.item_context) && Object.keys(row.item_context).length > 0
                ? [{
                  label: 'Metadaten',
                  attrs: {
                    title: 'Metadaten'
                  },
                  class: (r) => {
                    const defaultClasses = 'border-gray-600 text-gray-700 hover:bg-gray-600 hover:text-white'

                    if (!this.hasQuery || !this.queryMatchesContext(r)) {
                      return defaultClasses
                    }

                    return 'border-orange-mia hover:border-orange-mia-dark bg-orange-mia hover:bg-orange-mia-dark text-gray-100 hover:text-white'
                  },
                  onClick: (r) => {
                    this.previewableMetadataNotification = r
                  }
                }, ...baseActions]
                : baseActions
            }
          }
        }
      ],
      notifications: [],
      query: ''
    }
  },

  computed: {
    hasQuery() {
      return this.query.trim().length > 0
    },

    filteredNotifications() {
      if (!this.hasQuery) {
        return this.notifications
      }

      const query = this.query.trim()

      return this.notifications.filter((notification) => {
        if (notification.item_notification.toLowerCase().includes(query.toLowerCase())) {
          return true
        }

        if (notification.item_notification_label && notification.item_notification_label.toLowerCase().includes(query.toLowerCase())) {
          return true
        }

        return this.queryMatchesContext(notification)
      })
    }
  },

  created() {
    this.loadLog()
  },

  methods: {
    async loadLog() {
      if (this.$wait.is('loading car notification log')) {
        return
      }

      this.$wait.start('loading car notification log')
      const [error, response] = await getNotificationLog(this.carId)
      this.$wait.end('loading car notification log')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Log konnte nicht geladen werden']
        })
        return
      }

      this.notifications = response.data
    },

    getCustomerName(notification) {
      if (!notification.item_context || Array.isArray(notification.item_context) || !notification.item_context.customer) {
        return ''
      }

      const company = notification.item_context.customer.item_company ? `${notification.item_context.customer.item_company}, ` : ''
      const prefix = [notification.item_context.customer.item_salutation, notification.item_context.customer.item_title].filter(value => !isEmpty(value))
      const suffix = [notification.item_context.customer.item_name, notification.item_context.customer.item_firstname].filter(value => !isEmpty(value))

      return `${company}${prefix.join(' ')} ${suffix.join(', ')}`.trim()
    },

    queryMatchesContext(notification) {
      if (!notification.item_context || Array.isArray(notification.item_context) || Object.keys(notification.item_context).length === 0) {
        return false
      }

      const query = this.query.trim().toLowerCase()

      return Object.entries(notification.item_context).some(([contextId, values]) => {
        switch (contextId) {
          case 'car':
            return ['item_mobile_make', 'item_mobile_model', 'item_fin'].some(field => (values[field] || '').toLowerCase().includes(query))

          case 'customer':
            return ['item_firstname', 'item_name', 'item_company', 'item_email'].some(field => (values[field] || '').toLowerCase().includes(query))

          case 'seller':
          case 'dispatchEmployee':
            return ['item_firstname', 'item_lastname', 'item_email'].some(field => (values[field] || '').toLowerCase().includes(query))

          default:
            return false
        }
      })
    }
  }
}
</script>

<template>
  <mia-form>
    <mia-form-section
      title="Gesendete Benachrichtigungen"
      title-wrapper-class="bg-gray-100 rounded-t pb-4"
      title-wrapper-style="width: 100%"
      vertical
      no-padding
    >
      <mia-wait for="loading car notification log">
        <template #waiting>
          <div class="flex items-center justify-center py-8">
            <mia-wave-loader />
          </div>
        </template>

        <template #default>
          <div class="flex justify-between p-4 bg-gray-100">
            <div class="flex items-center w-3/12">
              <mia-feature-input
                v-model="query"
                class="w-full"
                label="Suchen"
                placeholder="Einträge durchsuchen..."
              />
            </div>

            <div class="flex items-center">
              {{ filteredNotifications.length }} {{ filteredNotifications.length === 1 ? 'Eintrag' : 'Einträge' }}{{ notifications.length !== filteredNotifications.length ? ' (gefiltert)' : '' }}
            </div>
          </div>

          <datatable
            class="w-full table-auto datatable"
            :data="filteredNotifications"
            :columns="columns"
          >
            <template #no-results>
              <div class="flex justify-center">
                {{ hasQuery ? 'Keine passenden Einträge gefunden.' : 'Es wurden noch keine Benachrichtigungen verschickt.' }}
              </div>
            </template>
          </datatable>

          <mia-notification-context-modal
            v-if="previewableMetadataNotification"
            id="car-notification-context-modal"
            :show="previewableMetadataNotification"
            :notification="previewableMetadataNotification"
            :highlight="[query]"
            @closed="previewableMetadataNotification = null"
          />

          <mia-notification-preview-modal
            v-if="previewableNotification"
            id="car-notification-preview-modal"
            :show="previewableNotification"
            :notification="previewableNotification"
            @closed="previewableNotification = null"
          />
        </template>
      </mia-wait>
    </mia-form-section>
  </mia-form>
</template>
